// Shared renderer for legal pages (Privacy Policy, Terms of Service, Cookie Policy)
// Reads current i18n language and renders the appropriate content with Startus branding
import { useTranslation } from 'react-i18next';
import logoUrl from '../assets/icons/logo-startus.svg';
import type { LocalizedLegalContent } from '@startus/content';

interface LegalPageProps {
  content: LocalizedLegalContent;
}

/**
 * Renders a legal document with Startus brand styling.
 * Falls back to English when current language is not Spanish.
 */
export function LegalPage({ content }: LegalPageProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('es') ? 'es' : 'en';
  const doc = content[lang];

  return (
    <div
      data-theme="dark"
      className="min-h-screen py-16 px-4"
      style={{
        background:
          'radial-gradient(ellipse 90% 55% at 50% 0%, #5c0000 0%, #200000 45%, #070000 100%)',
      }}
    >
      <div className="max-w-3xl mx-auto">
        <img src={logoUrl} alt="Startus" className="w-36 mb-12 select-none" draggable={false} />

        {/* Title with brand gradient */}
        <h1
          className="text-4xl font-bold mb-2"
          style={{
            background: 'linear-gradient(to right, #1aa1f8 0%, #ff1800 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          {doc.title}
        </h1>

        <p className="text-sm mb-10" style={{ color: 'rgba(255,255,255,0.4)' }}>
          {doc.lastUpdated}
        </p>

        {/* Multi-paragraph intro */}
        {doc.introParagraphs && doc.introParagraphs.length > 0 && (
          <div className="mb-10 space-y-4">
            {doc.introParagraphs.map((para, i) => (
              <p key={i} style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.75' }}>
                {para}
              </p>
            ))}
          </div>
        )}

        {/* Legacy single-paragraph intro */}
        {!doc.introParagraphs && doc.intro && (
          <p className="mb-10" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.75' }}>
            {doc.intro}
          </p>
        )}

        {doc.sections.map((section) => (
          <section key={section.title} className="mb-10">
            <h2 className="text-lg font-semibold mb-3" style={{ color: '#1aa1f8' }}>
              {section.title}
            </h2>

            {section.paragraphs?.map((para, i) => (
              <p
                key={i}
                className="mb-3"
                style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.75' }}
              >
                {para}
              </p>
            ))}

            {section.items && section.items.length > 0 && (
              <ul
                className="list-disc list-inside space-y-1 mt-2"
                style={{ color: 'rgba(255,255,255,0.7)' }}
              >
                {section.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
            )}

            {section.trailing && (
              <p className="mt-4" style={{ color: 'rgba(255,255,255,0.7)', lineHeight: '1.75' }}>
                {section.trailing}
              </p>
            )}
          </section>
        ))}
      </div>
    </div>
  );
}
